
import React, { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Sidebar } from "@/components/Sidebar";
import { Separator } from "@/components/ui/separator";
import { MobileNavigation } from "@/components/MobileNavigation";
import { useToast } from "@/components/ui/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/hooks/use-user";
import { useNavigate } from "react-router-dom";

const Settings = () => {
  const { user, isLoading } = useUser();
  const [language, setLanguage] = useState("pt-BR");
  const [theme, setTheme] = useState("system");
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    // Verificar se o usuário está autenticado
    if (!isLoading && !user) {
      navigate("/auth");
    }
    
    // Carregar configurações do usuário
    const loadUserSettings = async () => {
      if (!user) return;
      
      try {
        // Temporarily using manual queries until DB schema is updated
        setLanguage("pt-BR");
        setTheme("system");
        setNotificationsEnabled(true);
        setIsLoaded(true);
      } catch (error) {
        console.error("Erro ao carregar configurações:", error);
      } finally {
        setIsLoaded(true);
      }
    };
    
    if (user) {
      loadUserSettings();
    }
  }, [user, isLoading, navigate]);

  const saveSettings = async () => {
    if (!user) return;
    
    try {
      // We would need to update the profile schema to add these fields
      toast({
        title: "Configurações salvas",
        description: "Suas preferências foram atualizadas com sucesso.",
      });
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar suas configurações. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  if (isLoading || !isLoaded) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <div className="flex flex-1 pt-16">
          <Sidebar />
          <main className="flex-1 p-6 md:p-8 pt-8 md:ml-16">
            <div className="h-full flex items-center justify-center">
              <div className="animate-pulse">Carregando...</div>
            </div>
          </main>
        </div>
        <MobileNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <div className="flex flex-1 pt-16">
        <Sidebar />
        <main className="flex-1 p-4 md:p-8 pt-8 md:ml-16">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">Configurações</h1>
            
            <div className="bg-card rounded-lg border border-border p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4">Geral</h2>
              
              <div className="space-y-6">
                {/* Idioma */}
                <div>
                  <h3 className="text-md font-medium mb-3">Idioma</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Selecione o idioma de preferência para a interface do Ink Webtoons
                  </p>
                  
                  <RadioGroup 
                    defaultValue={language} 
                    onValueChange={setLanguage}
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="pt-BR" id="pt-BR" />
                      <Label htmlFor="pt-BR">Português (Brasil)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="en-US" id="en-US" />
                      <Label htmlFor="en-US">English (US)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="es-ES" id="es-ES" />
                      <Label htmlFor="es-ES">Español</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="fr-FR" id="fr-FR" />
                      <Label htmlFor="fr-FR">Français</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <Separator />
                
                {/* Tema */}
                <div>
                  <h3 className="text-md font-medium mb-3">Tema</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Escolha o tema de exibição do aplicativo
                  </p>
                  
                  <RadioGroup 
                    defaultValue={theme} 
                    onValueChange={setTheme}
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="light" id="light" />
                      <Label htmlFor="light">Claro</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="dark" id="dark" />
                      <Label htmlFor="dark">Escuro</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="system" id="system" />
                      <Label htmlFor="system">Sistema (automático)</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <Separator />
                
                {/* Notificações */}
                <div>
                  <h3 className="text-md font-medium mb-3">Notificações</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Gerenciar suas preferências de notificações
                  </p>
                  
                  <RadioGroup 
                    defaultValue={notificationsEnabled ? "enabled" : "disabled"} 
                    onValueChange={(value) => setNotificationsEnabled(value === "enabled")}
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="enabled" id="enabled" />
                      <Label htmlFor="enabled">Ativadas</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="disabled" id="disabled" />
                      <Label htmlFor="disabled">Desativadas</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
            </div>
            
            <div className="bg-card rounded-lg border border-border p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4">Conta</h2>
              
              <div className="space-y-6">
                {/* Privacidade */}
                <div>
                  <h3 className="text-md font-medium mb-2">Privacidade e segurança</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Gerencie suas configurações de privacidade e segurança
                  </p>
                  
                  <Button 
                    variant="outline" 
                    className="w-full md:w-auto"
                    onClick={() => navigate("/settings/privacy")}
                  >
                    Configurações de privacidade
                  </Button>
                </div>
                
                <Separator />
                
                {/* Sair da conta */}
                <div>
                  <h3 className="text-md font-medium mb-2">Sair da conta</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Desconectar-se do Ink Webtoons
                  </p>
                  
                  <Button 
                    variant="destructive" 
                    className="w-full md:w-auto"
                    onClick={async () => {
                      await supabase.auth.signOut();
                      navigate("/auth");
                    }}
                  >
                    Sair
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-4 mt-8">
              <Button variant="outline" onClick={() => navigate("/")}>
                Cancelar
              </Button>
              <Button onClick={saveSettings}>
                Salvar alterações
              </Button>
            </div>
          </div>
        </main>
      </div>
      <MobileNavigation />
    </div>
  );
};

export default Settings;
